import {
  FUEL_PROVIDER_OPTIONS,
  type FuelProvider,
  type ReminderDueState,
  type ReminderType,
  type VehicleStatus,
} from './types';

export const REMINDER_TYPE_DEFAULTS: Record<
  ReminderType,
  { intervalMonths: string; intervalKm: string; dueSoonDays: string; dueSoonKm: string }
> = {
  oil_change: { intervalMonths: '12', intervalKm: '15000', dueSoonDays: '30', dueSoonKm: '1000' },
  inspection: { intervalMonths: '12', intervalKm: '', dueSoonDays: '30', dueSoonKm: '' },
  hu_au: { intervalMonths: '24', intervalKm: '', dueSoonDays: '60', dueSoonKm: '' },
  tire_change: { intervalMonths: '6', intervalKm: '', dueSoonDays: '30', dueSoonKm: '' },
  insurance: { intervalMonths: '12', intervalKm: '', dueSoonDays: '30', dueSoonKm: '' },
  lease_end: { intervalMonths: '', intervalKm: '', dueSoonDays: '60', dueSoonKm: '' },
  other: { intervalMonths: '', intervalKm: '', dueSoonDays: '30', dueSoonKm: '' },
};

export function fuelProviderLabel(provider: FuelProvider | string): string {
  return FUEL_PROVIDER_OPTIONS.find((o) => o.value === provider)?.label ?? String(provider).toUpperCase();
}

export function formatFuelCardLabel(provider: FuelProvider | string, lastFour?: string | null): string {
  const name = fuelProviderLabel(provider);
  const digits = (lastFour ?? '').replace(/\D/g, '');
  return digits ? `${name} · ${digits}` : name;
}

export function formatKm(n: number | null | undefined): string {
  if (n == null || n <= 0) return 'Keine Angabe';
  return `${n.toLocaleString('de-DE')} km`;
}

export function formatCents(cents: number | null | undefined): string {
  if (cents == null) return '—';
  return (cents / 100).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
}

export function formatLiters(milli: number | null | undefined): string {
  if (milli == null) return '—';
  return `${(milli / 1000).toLocaleString('de-DE', { maximumFractionDigits: 1 })} L`;
}

export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toLocaleString('de-DE', { maximumFractionDigits: 1 })} MB`;
}

export function formatIsoDate(value: string | null | undefined): string {
  if (!value) return '—';
  const [y, m, d] = value.slice(0, 10).split('-');
  if (!y || !m || !d) return value;
  return `${d}.${m}.${y}`;
}

export function formatMonthYear(value: string | null | undefined): string {
  if (!value) return '—';
  const [y, m] = value.slice(0, 10).split('-');
  if (!y || !m) return value;
  return `${m}/${y}`;
}

export function daysText(days: number | null | undefined): string {
  if (days == null) return '';
  if (days < 0) return `${Math.abs(days)} Tage überfällig`;
  if (days === 0) return 'heute fällig';
  return `in ${days} Tagen`;
}

export function vehicleStatusLabel(status: VehicleStatus): string {
  if (status === 'workshop') return 'In Werkstatt';
  if (status === 'inactive') return 'Inaktiv';
  return 'Aktiv';
}

export function reminderStateLabel(state: ReminderDueState): string {
  if (state === 'overdue') return 'Überfällig';
  if (state === 'soon') return 'Bald fällig';
  if (state === 'done') return 'Erledigt';
  return 'Später fällig';
}

export function centsToEuroInput(cents: number | null | undefined): string {
  if (cents == null) return '';
  return (cents / 100).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function litersToInput(milli: number | null | undefined): string {
  if (milli == null) return '';
  return (milli / 1000).toLocaleString('de-DE', { maximumFractionDigits: 1 });
}

export function sanitizeDigitsInput(raw: string, maxDigits: number): string {
  return raw.replace(/\D/g, '').slice(0, maxDigits);
}

export function sanitizeDecimalInput(raw: string, fractionDigits: number): string {
  const cleaned = raw.replace(/[^\d.,]/g, '');
  if (!cleaned) return '';
  let sepIndex = -1;
  for (let i = cleaned.length - 1; i >= 0; i -= 1) {
    if (cleaned[i] === ',' || cleaned[i] === '.') {
      sepIndex = i;
      break;
    }
  }
  if (sepIndex === -1) return cleaned.replace(/\D/g, '').slice(0, 8);
  const sep = cleaned[sepIndex];
  const intPart = cleaned.slice(0, sepIndex).replace(/\D/g, '').slice(0, 8);
  const fracPart = cleaned.slice(sepIndex + 1).replace(/\D/g, '').slice(0, fractionDigits);
  if (sepIndex === cleaned.length - 1) return `${intPart}${sep}`;
  return `${intPart}${sep}${fracPart}`;
}

export function sanitizeVinInput(raw: string): string {
  return raw.replace(/[^a-zA-Z0-9]/g, '').toUpperCase().slice(0, 17);
}

export function sanitizePlateInput(raw: string): string {
  return raw.replace(/[^a-zA-Z0-9\s-]/g, '').toUpperCase().slice(0, 12);
}

export function parseEuroInput(value: string): number | null {
  const raw = value.trim().replace(/\s/g, '').replace(/\./g, '').replace(',', '.');
  if (!raw) return null;
  const n = Number.parseFloat(raw);
  if (!Number.isFinite(n) || n < 0) return null;
  return n;
}

export function validateVehicleYear(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const year = Number.parseInt(trimmed, 10);
  if (!/^\d{4}$/.test(trimmed) || year < 1970 || year > 2100) {
    return 'Baujahr muss zwischen 1970 und 2100 liegen.';
  }
  return null;
}

export function validateVehicleForm(values: {
  model: string;
  plate: string;
  year: string;
  fuel_limit: string;
}): string | null {
  if (!values.model.trim()) return 'Modell ist erforderlich.';
  if (!values.plate.trim()) return 'Kennzeichen ist erforderlich.';
  const yearError = validateVehicleYear(values.year);
  if (yearError) return yearError;
  if (values.fuel_limit.trim() && parseEuroInput(values.fuel_limit) == null) {
    return 'Tanklimit muss eine gültige Zahl sein.';
  }
  return null;
}

function parseOptionalPositive(value: string): number | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const n = Number.parseInt(trimmed.replace(/\./g, ''), 10);
  if (!Number.isFinite(n) || n <= 0) return null;
  return n;
}

export function addMonthsToDate(ymd: string, months: number): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(ymd) || months <= 0) return null;
  const y = Number(ymd.slice(0, 4));
  const m = Number(ymd.slice(5, 7));
  const d = Number(ymd.slice(8, 10));
  const utc = new Date(Date.UTC(y, m - 1 + months, 1));
  const lastDay = new Date(Date.UTC(utc.getUTCFullYear(), utc.getUTCMonth() + 1, 0)).getUTCDate();
  const next = new Date(Date.UTC(utc.getUTCFullYear(), utc.getUTCMonth(), Math.min(d, lastDay)));
  return `${next.getUTCFullYear()}-${String(next.getUTCMonth() + 1).padStart(2, '0')}-${String(next.getUTCDate()).padStart(2, '0')}`;
}

export function validateReminderForm(values: {
  dueOn: string;
  dueMileage: string;
  intervalMonths: string;
  intervalKm: string;
}): string | null {
  const hasDue = Boolean(values.dueOn.trim()) || Boolean(values.dueMileage.trim());
  const hasInterval = Boolean(values.intervalMonths.trim()) || Boolean(values.intervalKm.trim());
  if (!hasDue && !hasInterval) {
    return 'Bitte nächsten Termin, km-Fälligkeit oder Intervall angeben.';
  }
  if (values.intervalMonths.trim() && parseOptionalPositive(values.intervalMonths) == null) {
    return 'Zeitintervall muss eine positive Zahl sein.';
  }
  if (values.intervalKm.trim() && parseOptionalPositive(values.intervalKm) == null) {
    return 'Kilometerintervall muss eine positive Zahl sein.';
  }
  if (values.dueMileage.trim() && parseOptionalPositive(values.dueMileage) == null) {
    return 'Fälligkeit in Kilometern muss eine gültige Zahl sein.';
  }
  return null;
}

export function validateRepairForm(values: { title: string; cost: string }): string | null {
  if (!values.title.trim()) return 'Bezeichnung ist erforderlich.';
  if (values.cost.trim() && parseEuroInput(values.cost) == null) {
    return 'Kosten müssen eine gültige Zahl sein.';
  }
  return null;
}

export function validateCompleteForm(values: { performedOn: string; mileage: string; cost: string }): string | null {
  if (!values.performedOn.trim()) return 'Durchführungsdatum ist erforderlich.';
  if (values.mileage.trim() && parseOptionalPositive(values.mileage) == null && values.mileage.trim() !== '0') {
    return 'Kilometerstand muss eine gültige Zahl sein.';
  }
  if (values.cost.trim() && parseEuroInput(values.cost) == null) {
    return 'Kosten müssen eine gültige Zahl sein.';
  }
  return null;
}
